import React, { useCallback, useMemo } from 'react';
import PaginationTable, {
    PaginationTableProps,
} from '../../../../../ui/DataDisplay/Tables/PaginationTable/PaginationTable';
import { GetSongCollectionSongStatsParams, SongTiktokStats } from '@round/api';
import { CellProps, Column } from 'react-table';
import Skeleton from '../../../../../ui/Skeleton/Skeleton';
import styles from './SongsTable.module.css';
import Image from '../../../../../ui/Image/Image';
import { ReactComponent as MusicIcon } from '../../../../../assets/MusicIcon.svg';
import { formatNumberToKNotation, numberWithCommas, roundTo2Dp } from '../../../../../helpers';
import cn from 'classnames';
import BarsSmallChart from '../../../../../ui/DataDisplay/Charts/BarsSmallChart/BarsSmallChart';
import Button from '../../../../../ui/Buttons/Button/Button';
import { ReactComponent as CloseIcon } from '../../../../../assets/Close.svg';

type SongsTableProps = Omit<PaginationTableProps<SongTiktokStats>, 'columns'> & {
    ordering?: GetSongCollectionSongStatsParams['ordering'];
    onRemoveSongClicked: (row: SongTiktokStats) => void;
};

const SongsTable = ({ ordering, onRemoveSongClicked, noDataLabel = 'No data found', ...props }: SongsTableProps) => {
    const formatVideoCountBadgeValue = useCallback(
        (n: number) => {
            if (ordering?.includes('relative')) {
                return `${roundTo2Dp(n * 100)}%`;
            }

            return formatNumberToKNotation(n);
        },
        [ordering]
    );

    const columns: Column<SongTiktokStats>[] = useMemo(
        () => [
            {
                Header: 'Song',
                accessor: 'song',
                Cell: ({ value }) => {
                    if (props.loading) {
                        return <Skeleton />;
                    }

                    return (
                        <div className={styles.songContainer}>
                            <Image className={styles.songImage} src={value.image ?? ''} alt={value.title} />
                            <div className={styles.songDetails}>
                                <a
                                    href={value.spotify_song_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={styles.songTitle}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {value.title}
                                </a>
                                <p className={styles.songAuthor}>
                                    <MusicIcon className={styles.musicIcon} />
                                    {value.artists.map((a) => a.name).join(', ')}
                                </p>
                            </div>
                        </div>
                    );
                },
            },
            {
                Header: 'Audios',
                accessor: 'audio_count',
                Cell: ({ value }) => {
                    if (props.loading) {
                        return <Skeleton />;
                    }

                    return <>{numberWithCommas(value)}</>;
                },
            },
            {
                Header: 'Creations',
                accessor: 'total_video_count',
                Cell: ({ value, row: { original } }) => {
                    if (props.loading) {
                        return <Skeleton />;
                    }

                    const badgeValue =
                        ordering && ordering !== '-total_video_count'
                            ? Number(original[ordering.slice(1) as keyof SongTiktokStats])
                            : null;

                    return (
                        <>
                            <span>{formatNumberToKNotation(value)}</span>
                            {typeof badgeValue === 'number' && (
                                <span
                                    className={cn(styles.growthBadge, {
                                        [styles.increasing]: badgeValue >= 0,
                                        [styles.decreasing]: badgeValue < 0,
                                    })}
                                >
                                    {badgeValue > 0 && '+'}
                                    {formatVideoCountBadgeValue(badgeValue)}
                                </span>
                            )}
                        </>
                    );
                },
            },
            {
                Header: 'Growth',
                accessor: 'standardised_data',
                Cell: ({ value }) => {
                    if (props.loading) {
                        return <Skeleton />;
                    }

                    return (
                        <div className={styles.growth}>
                            <BarsSmallChart
                                color="linear-gradient(180deg, #93AAFF 0%, #6BC1FF 100%)"
                                values={value.daily_total_video_counts
                                    .slice(-7)
                                    .map((item) => ({ ...item, value: item.change }))}
                            />
                        </div>
                    );
                },
            },
            {
                Header: '',
                id: 'removeSongFromCollection',
                Cell: ({ row: { original } }: CellProps<SongTiktokStats>) => {
                    if (props.loading) {
                        return <Skeleton />;
                    }

                    return (
                        <Button
                            className={styles.removeSongButton}
                            onClick={(e) => {
                                e.stopPropagation();
                                onRemoveSongClicked(original);
                            }}
                        >
                            <CloseIcon />
                        </Button>
                    );
                },
            },
        ],
        [formatVideoCountBadgeValue, onRemoveSongClicked, ordering, props.loading]
    );

    return (
        <PaginationTable
            columns={columns}
            noDataLabel={noDataLabel}
            tableClassName={styles.table}
            autoResetPage={false}
            {...props}
        />
    );
};

export default SongsTable;
